import { useCallback, useEffect, useState } from 'react';

import { ActionsMenu } from '@/components/ActionsMenu';
import { FormModal } from '@/components/Modals';
import { ScopePicker } from '@/components/ScopePicker';
import {
  keysAPI,
  KeyApiError,
  type ApiKeyRow,
  type KeyListResponse,
  type MintedKey,
} from '@/lib/apiKeysApi';

/**
 * /dashboard/developer — admin API keys for the /admin/v1 surface.
 *
 * Two things this page has to get right, both of which are about the
 * operator not being surprised later:
 *
 *   1. THE SECRET IS SHOWN ONCE. There is no "reveal" — only a sha256 is
 *      stored. The reveal panel is therefore loud, persistent until
 *      dismissed, and says plainly that closing it loses the key.
 *   2. THE ENVIRONMENT IS THE KEY'S IDENTITY. A key minted here works
 *      only on the deployment that minted it. The console has an env
 *      toggle, so a key list is always shown next to which deployment
 *      you are looking at — otherwise "why is my key rejected" becomes a
 *      support question instead of an obvious fact.
 */
export function DeveloperPage() {
  const [data, setData] = useState<KeyListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<MintedKey | null>(null);
  const [editing, setEditing] = useState<ApiKeyRow | null>(null);

  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [ttl, setTtl] = useState<string>('never');

  const load = useCallback(async () => {
    try {
      setData(await keysAPI.list());
      setError(null);
    } catch (e) {
      setError(e instanceof KeyApiError ? e.message : 'Could not load keys.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      setError(null);
    } catch (e) {
      setError(e instanceof KeyApiError ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const onMint = () =>
    run(async () => {
      const r = await keysAPI.mint({
        label,
        scopes,
        ttl_days: ttl === 'never' ? null : Number(ttl),
      });
      setMinted(r);
      setLabel('');
      setScopes([]);
      await load();
    });

  const onRotate = (row: ApiKeyRow) =>
    run(async () => {
      const r = await keysAPI.rotate(row.id);
      setMinted(r);
      await load();
    });

  const onRevoke = (row: ApiKeyRow) =>
    run(async () => {
      await keysAPI.revoke(row.id);
      await load();
    });

  if (!data) {
    return <div style={{ color: 'var(--muted)' }}>{error ?? 'Loading…'}</div>;
  }

  const envLabel = data.environment === 'dev' ? 'Development' : 'Production';

  return (
    <div style={{ maxWidth: 900 }}>
      <h1 className="serif" style={{ fontSize: 28, marginBottom: 4 }}>
        Developer
      </h1>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        API keys for the <code>/admin/v1</code> surface — creating and managing
        synthetic test profiles.
      </p>

      <div style={banner}>
        Keys minted here work on <strong>{envLabel}</strong> only. Presenting one
        to the other deployment is rejected outright, never silently accepted.
      </div>

      {error && <div style={errorBox}>{error}</div>}

      {minted && <RevealPanel minted={minted} onDismiss={() => setMinted(null)} />}

      <section style={card}>
        <h2 style={h2}>Mint a key</h2>
        <label style={fieldLabel}>Label</label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. profile-seeder"
          style={input}
        />

        <div style={{ ...fieldLabel, marginTop: 16 }} id="mint-scopes-label">Scopes</div>
        <div role="group" aria-labelledby="mint-scopes-label">
          <ScopePicker
            groups={data.scope_groups}
            grantable={data.grantable_scopes}
            selected={scopes}
            onChange={setScopes}
            disabled={busy}
          />
        </div>

        <label style={{ ...fieldLabel, marginTop: 16 }}>Expires</label>
        <select value={ttl} onChange={(e) => setTtl(e.target.value)} style={input}>
          <option value="never">Never</option>
          <option value="30">In 30 days</option>
          <option value="90">In 90 days</option>
          <option value="365">In a year</option>
        </select>

        <button
          onClick={onMint}
          disabled={busy || !label.trim() || scopes.length === 0}
          style={primaryButton}
        >
          {busy ? 'Working…' : 'Mint key'}
        </button>
      </section>

      <section style={{ ...card, marginTop: 24 }}>
        <h2 style={h2}>Your keys</h2>
        {data.keys.length === 0 ? (
          <p style={{ color: 'var(--muted)' }}>No keys yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                <th style={th}>Label</th>
                <th style={th}>Env</th>
                <th style={th}>Scopes</th>
                <th style={{ ...th, whiteSpace: 'nowrap' }}>Last used</th>
                <th style={th}>Expires</th>
                {/* relative: anchors the hidden label so it can't escape the
                    table's sideways scroller and widen the page on a phone. */}
                <th style={{ ...th, width: 1, position: 'relative' }}>
                  <span style={visuallyHidden}>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.keys.map((k) => (
                <tr key={k.id} style={{ opacity: k.active ? 1 : 0.5 }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {k.label}
                    {!k.active && (
                      <span style={{ color: 'var(--muted)' }}>
                        {' '}
                        — {k.revoked_at ? (k.rotated_to_id ? 'rotated' : 'revoked') : 'expired'}
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    <code>{k.env}</code>
                  </td>
                  <td style={{ ...td, fontSize: 12 }}>{k.scopes.join(', ')}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {k.last_used_at ? fmt(k.last_used_at) : 'never'}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {k.expires_at ? fmt(k.expires_at) : 'never'}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {k.active && (
                      <ActionsMenu
                        label={`Actions for ${k.label}`}
                        disabled={busy}
                        items={[
                          { label: 'Edit scopes', onSelect: () => setEditing(k) },
                          { label: 'Rotate', onSelect: () => onRotate(k) },
                          { label: 'Revoke', onSelect: () => onRevoke(k), tone: 'danger', separated: true },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {editing && (
        <EditScopesModal
          key={editing.id}
          row={editing}
          data={data}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Edit a live key's scopes. The secret is untouched, so a running agent
 * keeps its credential and picks up the new permissions on its next call.
 *
 * Scopes the key carries but the operator no longer holds are "stale":
 * shown checked and locked, and left out of the payload, so saving drops
 * them (the server would refuse them anyway).
 */
function EditScopesModal({
  row,
  data,
  onClose,
  onSaved,
}: {
  row: ApiKeyRow;
  data: KeyListResponse;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const stale = row.scopes.filter((s) => !data.grantable_scopes.includes(s));
  const [selected, setSelected] = useState<string[]>(
    row.scopes.filter((s) => data.grantable_scopes.includes(s)),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    try {
      await keysAPI.updateScopes(row.id, selected);
      await onSaved();
    } catch (e) {
      setError(e instanceof KeyApiError ? e.message : 'Could not save scopes.');
      setBusy(false);
    }
  };

  return (
    <FormModal
      open
      title={`Edit scopes: ${row.label}`}
      confirmLabel="Save"
      confirmDisabled={selected.length === 0}
      busy={busy}
      onCancel={onClose}
      onConfirm={() => void save()}
    >
      {error && <div style={{ ...errorBox, marginTop: 0, marginBottom: 12 }}>{error}</div>}
      <ScopePicker
        groups={data.scope_groups}
        grantable={data.grantable_scopes}
        selected={selected}
        onChange={setSelected}
        stale={stale}
        disabled={busy}
      />
    </FormModal>
  );
}

/**
 * The one-and-only sighting of the plaintext. Deliberately not a toast:
 * it must survive until explicitly dismissed, because dismissing it is
 * irreversible and the operator needs time to paste it somewhere safe.
 */
function RevealPanel({ minted, onDismiss }: { minted: MintedKey; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={reveal}>
      <strong>Copy this key now — it will never be shown again.</strong>
      <p style={{ color: 'var(--muted)', fontSize: 13, margin: '4px 0 12px' }}>
        Only a hash is stored. If you lose it, rotate the key to get a new one.
      </p>
      <code style={secretBox}>{minted.secret}</code>
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button
          style={primaryButton}
          onClick={() => {
            void navigator.clipboard.writeText(minted.secret);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button style={smallButton} onClick={onDismiss}>
          I've saved it
        </button>
      </div>
    </div>
  );
}

function fmt(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const card: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: 24,
  marginTop: 24,
};
const h2: React.CSSProperties = { fontSize: 16, margin: '0 0 16px' };
const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  color: 'var(--muted)',
  marginBottom: 6,
};
const input: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--line-strong)',
  borderRadius: 8,
  background: 'var(--bg)',
  color: 'inherit',
};
const primaryButton: React.CSSProperties = {
  marginTop: 20,
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--coral, #e26d5c)',
  color: '#fff',
  cursor: 'pointer',
};
const smallButton: React.CSSProperties = {
  padding: '4px 10px',
  marginLeft: 6,
  borderRadius: 6,
  border: '1px solid var(--line-strong)',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
};
const banner: React.CSSProperties = {
  marginTop: 16,
  padding: '10px 14px',
  borderRadius: 8,
  background: 'var(--surface)',
  border: '1px solid var(--line-strong)',
  fontSize: 14,
};
const errorBox: React.CSSProperties = {
  ...banner,
  borderColor: '#c0392b',
  color: '#c0392b',
};
const reveal: React.CSSProperties = {
  ...card,
  borderColor: 'var(--coral, #e26d5c)',
  borderWidth: 2,
};
const secretBox: React.CSSProperties = {
  display: 'block',
  padding: 12,
  background: 'var(--bg)',
  border: '1px solid var(--line-strong)',
  borderRadius: 8,
  wordBreak: 'break-all',
  fontSize: 13,
};
const th: React.CSSProperties = {
  padding: '8px 6px',
  borderBottom: '1px solid var(--line)',
  fontWeight: 500,
};
const td: React.CSSProperties = { padding: '10px 6px', borderBottom: '1px solid var(--line)' };

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};
